import { useEffect, useState } from 'react';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/shared/ui/sheet';
import { dateCourte, getPreuve } from '@/shared/api/bridge';
import { refCourte } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

/**
 * La preuve d'effacement d'un ordre, en tiroir à côté du journal.
 *
 * LA RÈGLE QUI COMPTE LE PLUS ICI
 *
 * « 0 Wh » et « pas de mesure » ne sont pas le même chiffre. Le second ne s'affiche
 * jamais comme un effacement nul : il dit POURQUOI rien ne se chiffre (ordre refusé,
 * ordre simulé, thermostat sans compteur, machine muette, un seul relevé). Voir
 * `services/bridge/journal_agregateur.preuve`.
 */
export default function TiroirPreuve({ agregateur, ligne, surFermeture }) {
  const [preuve, setPreuve] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!ligne) return undefined;
    let vivant = true;
    setPreuve(null);
    setErreur(null);
    getPreuve(agregateur, ligne.ordre_id)
      .then((p) => vivant && setPreuve(p))
      .catch((exc) => vivant && setErreur(exc));
    return () => { vivant = false; };
  }, [agregateur, ligne]);

  const chiffrable = preuve && preuve.energie_wh !== null && preuve.energie_wh !== undefined;

  return (
    <Sheet open={Boolean(ligne)} onOpenChange={(ouvert) => !ouvert && surFermeture()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="bridge-preuve">
        <SheetHeader>
          <SheetTitle>Preuve de l&apos;ordre</SheetTitle>
          <SheetDescription>
            {ligne && <>{ligne.ordre} sur <code title={ligne.ref}>{refCourte(ligne.ref)}</code>, reçu le {dateCourte(ligne.recu_le)}</>}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-4 text-[13px]">
          <ErreurBloc erreur={erreur} testid="bridge-preuve-erreur" />
          {!preuve && !erreur && <Skeleton className="h-32 w-full" />}

          {preuve && (
            <>
              <div className={`rounded-md border px-4 py-3 ${chiffrable
                ? 'border-emerald-200 bg-emerald-50' : 'border-neutral-200 bg-neutral-50'}`}>
                {chiffrable ? (
                  <p className="text-[22px] font-semibold tabular-nums text-emerald-900"
                     data-testid="bridge-preuve-energie">
                    {preuve.energie_wh} Wh
                    <span className="block text-[12.5px] font-normal text-emerald-800">déplacés sur la fenêtre</span>
                  </p>
                ) : (
                  <p className="text-neutral-700" data-testid="bridge-preuve-motif">
                    <strong className="block">Aucune énergie chiffrable.</strong>
                    {preuve.motif}
                  </p>
                )}
              </div>

              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-neutral-600">
                <dt>Fenêtre</dt>
                <dd className="tabular-nums">
                  {dateCourte(preuve.fenetre?.debut)} → {dateCourte(preuve.fenetre?.fin)}
                </dd>
                <dt>Points relus</dt>
                <dd className="tabular-nums">{preuve.points}</dd>
              </dl>

              {Object.keys(preuve.compteurs || {}).length > 0 && (
                <table className="w-full text-[12.5px] tabular-nums">
                  <thead className="text-left text-neutral-500">
                    <tr><th className="font-normal">Compteur</th><th className="font-normal">Relevés</th>
                      <th className="text-right font-normal">Écart</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(preuve.compteurs).map(([nom, c]) => (
                      <tr key={nom} className="border-t border-neutral-100">
                        <td className="py-1.5"><code className="text-[11.5px]">{nom}</code>
                          <span className="block text-[11.5px] text-neutral-400">{c.sens}</span></td>
                        <td>{c.premier} → {c.dernier}</td>
                        <td className="text-right">{c.delta_wh === null ? '—' : `${c.delta_wh} Wh`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
